import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { animate, createScope, onScroll, stagger, svg, utils } from 'animejs';

type AnimeScope = ReturnType<typeof createScope>;

// The SPA scrolls inside #root, not the window; anime.js ScrollObserver
// defaults to the window, so resolve the real scroll container explicitly.
function findScrollContainer(el: HTMLElement): HTMLElement | undefined {
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (/(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
  }
  return undefined;
}

/**
 * Landing-page motion, scoped to the marketing home root.
 * Reduced-motion (and no-JS) users get the static page: elements are only
 * hidden inside the motion branch, immediately before their animation exists.
 */
export function useLandingMotion(rootRef: RefObject<HTMLElement | null>) {
  const scopeRef = useRef<AnimeScope | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    scopeRef.current = createScope({
      root,
      mediaQueries: { reduceMotion: '(prefers-reduced-motion: reduce)' },
    }).add((self) => {
      if (self?.matches.reduceMotion) return;

      const $$ = (selector: string) => Array.from(root.querySelectorAll<HTMLElement>(selector));
      const scrollContainer = findScrollContainer(root);
      const scrollOpts = scrollContainer ? { container: scrollContainer } : {};

      // Hero: the bbox frame draws itself around the keyword after the CSS
      // entrance settles.
      const heroFramePaths = $$('[data-bbox-frame] [data-draw]');
      if (heroFramePaths.length) {
        const drawable = svg.createDrawable(heroFramePaths);
        utils.set(drawable, { draw: '0 0' });
        animate(drawable, {
          draw: '0 1',
          ease: 'inOut(2)',
          duration: 900,
          delay: 1000,
        });
      }

      // Scroll-staggered reveals: each group plays once as it enters the viewport.
      for (const group of $$('[data-reveal-group]')) {
        const items = Array.from(group.querySelectorAll<HTMLElement>('[data-reveal]'));
        if (!items.length) continue;
        utils.set(items, { opacity: 0, translateY: '22px' });
        animate(items, {
          opacity: 1,
          translateY: '0px',
          duration: 700,
          ease: 'out(3)',
          delay: stagger(85),
          autoplay: onScroll({ ...scrollOpts, target: group, enter: 'bottom-=60 top', repeat: false }),
        });
      }

      // Feature glyphs draw their strokes when the extraction grid scrolls in.
      const glyphGrid = $$('[data-glyph-grid]')[0];
      const glyphPaths = $$('[data-glyph] [data-draw]');
      if (glyphGrid && glyphPaths.length) {
        const drawable = svg.createDrawable(glyphPaths);
        utils.set(drawable, { draw: '0 0' });
        animate(drawable, {
          draw: '0 1',
          ease: 'inOut(2)',
          duration: 800,
          delay: stagger(90),
          autoplay: onScroll({ ...scrollOpts, target: glyphGrid, enter: 'bottom-=80 top', repeat: false }),
        });
      }

      // Confidence score counts up to its resting value.
      const counter = $$('[data-confidence-counter]')[0];
      if (counter) {
        const score = { value: 0 };
        counter.textContent = '0.00';
        animate(score, {
          value: 0.97,
          duration: 1500,
          ease: 'out(3)',
          onUpdate: () => {
            counter.textContent = score.value.toFixed(2);
          },
          autoplay: onScroll({ ...scrollOpts, target: counter, enter: 'bottom-=40 top', repeat: false }),
        });
      }
    });

    return () => scopeRef.current?.revert();
  }, [rootRef]);
}
